import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  toDatetimeLocal,
  computeSuggestionFill,
  shouldSuggestTitleTime,
} from "../lib/recordedTime";
import { formatMeetingDate } from "../lib/timestamps";

// Editable recorded date/time for a meeting + a one-click suggestion when the
// title carries a different date/time. recorded_at anchors every transcript
// wall-clock timestamp, so fixing it here re-anchors them at render — no
// re-transcription. The suggestion (titleRecordedAt) is advisory; nothing is
// written until the user edits the field or clicks "Use it".
export function RecordedTimeEditor({
  meetingId,
  recordedAt,
  titleRecordedAt,
  isLocked,
  onSaved,
}: {
  meetingId: string;
  recordedAt: string | null;
  titleRecordedAt: string | null;
  isLocked: boolean;
  onSaved: (nextIso: string) => void;
}) {
  const [value, setValue] = useState(() => toDatetimeLocal(recordedAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);

  // Re-sync if the parent's recorded_at changes (e.g. after a refresh).
  useEffect(() => {
    setValue(toDatetimeLocal(recordedAt));
  }, [recordedAt]);

  const showSuggestion =
    !isLocked && shouldSuggestTitleTime(titleRecordedAt, recordedAt);

  async function save(localValue: string) {
    if (!localValue || savingRef.current) return;
    const parsed = new Date(localValue);
    if (Number.isNaN(parsed.getTime())) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from("meetings")
      .update({ recorded_at: parsed.toISOString() })
      .eq("id", meetingId);
    savingRef.current = false;
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    onSaved(parsed.toISOString());
  }

  function useTitleTime() {
    if (!titleRecordedAt) return;
    const fill = computeSuggestionFill(titleRecordedAt, recordedAt);
    setValue(fill);
    void save(fill);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <label htmlFor="recorded-at" className="muted" style={{ fontSize: 12 }}>
        Recorded
      </label>
      <input
        id="recorded-at"
        type="datetime-local"
        aria-label="Recorded date and time"
        value={value}
        disabled={isLocked || saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (value && value !== toDatetimeLocal(recordedAt)) void save(value);
        }}
        style={{
          padding: "3px 6px",
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          fontSize: 12,
        }}
      />
      {showSuggestion && titleRecordedAt && (
        <span className="muted" style={{ fontSize: 12 }}>
          Title says{" "}
          <strong>
            {formatMeetingDate(computeSuggestionFill(titleRecordedAt, recordedAt))}
            {titleRecordedAt.includes("T") ? ` ${titleRecordedAt.slice(11, 16)}` : ""}
          </strong>{" "}
          <button
            type="button"
            className="secondary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={useTitleTime}
            disabled={saving}
            style={{ fontSize: 12, padding: "2px 8px" }}
          >
            Use it
          </button>
        </span>
      )}
      {error && (
        <span style={{ color: "var(--danger, #c0392b)", fontSize: 12 }}>{error}</span>
      )}
    </div>
  );
}
